import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { lazyWithReload as lazy } from '@/lib/lazyWithReload';
import OperationsPage from '../pages/back-office/operations/page';
import HotelSetupPage from '../pages/back-office/hotel-setup/page';

// Manager Pages
const ManagerOperationsPage = lazy(() => import('../pages/back-office/manager/operations/page'));
const ManagerApprovalsPage = lazy(() => import('../pages/back-office/manager/approvals/page'));
const ManagerPerformancePage = lazy(() => import('../pages/back-office/manager/performance/page'));
const ManagerReportsPage = lazy(() => import('../pages/back-office/manager/reports/page'));
const ManagerCommunicationPage = lazy(() => import('../pages/back-office/manager/communication/page'));
const ManagerMorePage = lazy(() => import('../pages/back-office/manager/more/page'));

// Super Admin Pages
const SuperAdminTenantsPage = lazy(() => import('../pages/back-office/super-admin/tenants/page'));
const SuperAdminPlatformPage = lazy(() => import('../pages/back-office/super-admin/platform/page'));
const SuperAdminMarketplacePage = lazy(() => import('../pages/back-office/super-admin/marketplace/page'));
const SuperAdminAnalyticsPage = lazy(() => import('../pages/back-office/super-admin/analytics/page'));
const SuperAdminSecurityPage = lazy(() => import('../pages/back-office/super-admin/security/page'));
const SuperAdminMorePage = lazy(() => import('../pages/back-office/super-admin/more/page'));

const LoginPage = lazy(() => import('../pages/login/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const SetPasswordPage = lazy(() => import('../pages/set-password/page'));

// Back Office Pages
const BackOfficePage = lazy(() => import('../pages/back-office/page'));
const DashboardPage = lazy(() => import('../pages/back-office/dashboard/page'));
const EventsBackOfficePage = lazy(() => import('../pages/back-office/events/page'));
const EventCalendarPage = lazy(() => import('../pages/back-office/events/calendar/page'));
const AccommodationRequestsPage = lazy(() => import('../pages/back-office/events/accommodation/page'));

// Reservations
const ReservationsPage = lazy(() => import('../pages/back-office/reservations/page'));
const ReservationDetailsPage = lazy(() => import('../pages/back-office/reservations/details/page'));
const GroupBookingsPage = lazy(() => import('../pages/back-office/reservations/group/page'));

// Front Office
const FrontOfficePage = lazy(() => import('../pages/back-office/front-office/page'));
const CheckInOutPage = lazy(() => import('../pages/back-office/front-office/check-in-out/page'));
const RoomAssignmentPage = lazy(() => import('../pages/back-office/front-office/room-assignment/page'));
const GuestFolioPage = lazy(() => import('../pages/back-office/front-office/folio/page'));
const GuestRequestsPage = lazy(() => import('../pages/back-office/front-office/guest-requests/page'));
const GroupArrivalsPage = lazy(() => import('../pages/back-office/front-office/group-arrivals/page'));
const GroupCheckInOutPage = lazy(() => import('../pages/back-office/front-office/group-check-in-out/page'));
const GroupFolioPage = lazy(() => import('../pages/back-office/front-office/group-folio/page'));
const GroupRoomAssignmentPage = lazy(() => import('../pages/back-office/front-office/group-room-assignment/page'));

// Rooms & Inventory
const RoomsPage = lazy(() => import('../pages/back-office/rooms/page'));
const RoomCalendarPage = lazy(() => import('../pages/back-office/rooms/calendar/page'));
const RoomStatusPage = lazy(() => import('../pages/back-office/rooms/status/page'));
const MaintenancePage = lazy(() => import('../pages/back-office/rooms/maintenance/page'));

// Rates & Revenue
const RatesPage = lazy(() => import('../pages/back-office/rates/page'));
const RatePlansPage = lazy(() => import('../pages/back-office/rates/plans/page'));
const PackagesPage = lazy(() => import('../pages/back-office/rates/packages/page'));
const RestrictionsPage = lazy(() => import('../pages/back-office/rates/restrictions/page'));
const RateProtectionPage = lazy(() => import('../pages/back-office/rates/rate-protection/page'));

// Housekeeping
const HousekeepingPage = lazy(() => import('../pages/back-office/housekeeping/page'));
const RoomStatusHKPage = lazy(() => import('../pages/back-office/housekeeping/room-status/page'));
const TasksPage = lazy(() => import('../pages/back-office/housekeeping/tasks/page'));

// Finance
const FinancePage = lazy(() => import('../pages/back-office/finance/page'));
const NightAuditPage = lazy(() => import('../pages/back-office/finance/night-audit/page'));
const CashieringPage = lazy(() => import('../pages/back-office/finance/cashiering/page'));
const RefundsPage = lazy(() => import('../pages/back-office/finance/refunds/page'));
const PaymentOperationsPage = lazy(() => import('../pages/back-office/finance/payment-operations/page'));
const AccountsPage = lazy(() => import('../pages/back-office/finance/accounts/page'));
const BillingPage = lazy(() => import('../pages/back-office/finance/billing/page'));
const ReceivablesPage = lazy(() => import('../pages/back-office/finance/receivables/page'));
const GroupFinancePage = lazy(() => import('../pages/back-office/finance/group/page'));
const UniversityBillingPage = lazy(() => import('../pages/back-office/finance/university/page'));
const ReconciliationPage = lazy(() => import('../pages/back-office/finance/reconciliation/page'));
const AuditControlsPage = lazy(() => import('../pages/back-office/finance/audit/page'));
const FinanceSettingsPage = lazy(() => import('../pages/back-office/finance/settings/page'));

// Reports
const ReportsPage = lazy(() => import('../pages/back-office/reports/page'));
const OccupancyReportPage = lazy(() => import('../pages/back-office/reports/occupancy/page'));
const RevenueReportPage = lazy(() => import('../pages/back-office/reports/revenue/page'));
const GuestReportPage = lazy(() => import('../pages/back-office/reports/guests/page'));

// Settings
const SettingsPage = lazy(() => import('../pages/back-office/settings/page'));
const UsersPage = lazy(() => import('../pages/back-office/settings/users/page'));
const RoleManagementPage = lazy(() => import('../pages/back-office/settings/roles/page'));
const SecuritySettingsPage = lazy(() => import('../pages/back-office/settings/security/page'));

// Demo
const DemoSwitcherPage = lazy(() => import('../pages/demo/role-switcher/page'));

// Error Pages
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Audit Log
const AuditLogPage = lazy(() => import('../pages/back-office/audit-log/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/back-office" replace />
  },
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />
  },
  {
    path: '/set-password',
    element: <SetPasswordPage />
  },

  // Back Office Routes
  {
    path: '/back-office',
    element: <BackOfficePage />,
    children: [
      {
        index: true,
        element: <DashboardPage />
      },
      {
        path: 'dashboard',
        element: <DashboardPage />
      },

      // Reservations
      {
        path: 'reservations',
        element: <ReservationsPage />
      },
      {
        path: 'reservations/:id',
        element: <ReservationDetailsPage />
      },
      {
        path: 'reservations/group',
        element: <GroupBookingsPage />
      },

      // Front Office
      {
        path: 'front-office',
        element: <FrontOfficePage />
      },
      {
        path: 'front-office/check-in-out',
        element: <CheckInOutPage />
      },
      {
        path: 'front-office/room-assignment',
        element: <RoomAssignmentPage />
      },
      {
        path: 'front-office/folio',
        element: <GuestFolioPage />
      },
      {
        path: 'front-office/folio/:guestId',
        element: <GuestFolioPage />
      },
      {
        path: 'front-office/guest-requests',
        element: <GuestRequestsPage />
      },
      {
        path: 'front-office/group-arrivals',
        element: <GroupArrivalsPage />
      },
      {
        path: 'front-office/group-check-in-out',
        element: <GroupCheckInOutPage />
      },
      {
        path: 'front-office/group-folio',
        element: <GroupFolioPage />
      },
      {
        path: 'front-office/group-room-assignment',
        element: <GroupRoomAssignmentPage />
      },

      // Rooms & Inventory
      {
        path: 'rooms',
        element: <RoomsPage />
      },
      {
        path: 'rooms/calendar',
        element: <RoomCalendarPage />
      },
      {
        path: 'rooms/status',
        element: <RoomStatusPage />
      },
      {
        path: 'rooms/maintenance',
        element: <MaintenancePage />
      },

      // Rates & Revenue
      {
        path: 'rates',
        element: <RatesPage />
      },
      {
        path: 'rates/plans',
        element: <RatePlansPage />
      },
      {
        path: 'rates/packages',
        element: <PackagesPage />
      },
      {
        path: 'rates/restrictions',
        element: <RestrictionsPage />
      },
      {
        path: 'rates/rate-protection',
        element: <RateProtectionPage />
      },

      // Housekeeping
      {
        path: 'housekeeping',
        element: <HousekeepingPage />
      },
      {
        path: 'housekeeping/room-status',
        element: <RoomStatusHKPage />
      },
      {
        path: 'housekeeping/tasks',
        element: <TasksPage />
      },

      // Finance
      {
        path: 'finance',
        element: <FinancePage />
      },
      {
        path: 'finance/night-audit',
        element: <NightAuditPage />
      },
      {
        path: 'finance/cashiering',
        element: <CashieringPage />
      },
      {
        path: 'finance/refunds',
        element: <RefundsPage />
      },
      {
        path: 'finance/payment-operations',
        element: <PaymentOperationsPage />
      },
      {
        path: 'finance/accounts',
        element: <AccountsPage />
      },
      {
        path: 'finance/billing',
        element: <BillingPage />
      },
      {
        path: 'finance/receivables',
        element: <ReceivablesPage />
      },
      {
        path: 'finance/group',
        element: <GroupFinancePage />
      },
      {
        path: 'finance/university',
        element: <UniversityBillingPage />
      },
      {
        path: 'finance/reconciliation',
        element: <ReconciliationPage />
      },
      {
        path: 'finance/audit',
        element: <AuditControlsPage />
      },
      {
        path: 'finance/settings',
        element: <FinanceSettingsPage />
      },

      // Reports
      {
        path: 'reports',
        element: <ReportsPage />
      },
      {
        path: 'reports/occupancy',
        element: <OccupancyReportPage />
      },
      {
        path: 'reports/revenue',
        element: <RevenueReportPage />
      },
      {
        path: 'reports/guests',
        element: <GuestReportPage />
      },

      // Settings
      {
        path: 'settings',
        element: <SettingsPage />
      },
      {
        path: 'settings/users',
        element: <UsersPage />
      },
      {
        path: 'settings/roles',
        element: <RoleManagementPage />
      },
      {
        path: 'settings/security',
        element: <SecuritySettingsPage />
      },

      // Hotel Setup (Admin)
      {
        path: 'hotel-setup',
        element: <HotelSetupPage />
      },

      // Operations (Admin)
      {
        path: 'operations',
        element: <OperationsPage />
      },

      // Manager Pages (Executive Workspace)
      {
        path: 'manager/operations',
        element: <ManagerOperationsPage />
      },
      {
        path: 'manager/approvals',
        element: <ManagerApprovalsPage />
      },
      {
        path: 'manager/performance',
        element: <ManagerPerformancePage />
      },
      {
        path: 'manager/reports',
        element: <ManagerReportsPage />
      },
      {
        path: 'manager/communication',
        element: <ManagerCommunicationPage />
      },
      {
        path: 'manager/more',
        element: <ManagerMorePage />
      },

      // Super Admin Pages (Platform Governance)
      {
        path: 'super-admin/tenants',
        element: <SuperAdminTenantsPage />
      },
      {
        path: 'super-admin/platform',
        element: <SuperAdminPlatformPage />
      },
      {
        path: 'super-admin/marketplace',
        element: <SuperAdminMarketplacePage />
      },
      {
        path: 'super-admin/analytics',
        element: <SuperAdminAnalyticsPage />
      },
      {
        path: 'super-admin/security',
        element: <SuperAdminSecurityPage />
      },
      {
        path: 'super-admin/more',
        element: <SuperAdminMorePage />
      },

      // Audit Log
      {
        path: 'audit-log',
        element: <AuditLogPage />
      },

      // Events Management
      {
        path: 'events',
        element: <EventsBackOfficePage />
      },
      {
        path: 'events/calendar',
        element: <EventCalendarPage />
      },
      {
        path: 'events/accommodation',
        element: <AccommodationRequestsPage />
      }
    ]
  },

  {
    path: '/demo/role-switcher',
    element: <DemoSwitcherPage />
  },

  // Error Routes
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
